import { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";

import { InputButton } from "../../../../../../components/input-button/InputButton";
import { InputField } from "../../../../../../components/input-field/InputField";
import { InputSelect } from "../../../../../../components/input-select/InputSelect";
import { InviteManager } from "./modals/invite_manager/InviteManager";
import { useAuthenticationContext } from "../../../../../../context/UserContext";
import { CountryService } from "../../../../../../services/CountryService";
import { BusinessOwnerService } from "../../../../../../services/BusinessOwnerService";
import CityService from "../../../../../../services/CityService";

interface BranchProps {
  id: number;
  name: string;
  address: string;
  countryId: number;
  cityId: number;
  latitude: string;
  longitude: string;
  manager: any;
  loadOverview: () => void;
}

const useStyles = makeStyles((theme) => ({
  main: {
    width: "100%",
    height: "100%",
    flexGrow: 1,
  },
  formSectionTitle: {
    fontWeight: 400,
    fontSize: 24,
  },
  managingContainer: {
    borderRadius: 26,
    borderWidth: 2,
    borderColor: "#36B516",
    borderStyle: "solid",
    marginLeft: 10,
    padding: 5,
  },
  managing: {
    fontSize: 14,
    fontWeight: 700,
    color: "#36B516",
  },
  warningText: {
    fontSize: 15,
    fontWeight: 400,
    color: "#FF2929",
  },
  inviteAManager: {
    backgroundColor: "#36B516",
    color: "#FFFFFF",
    textTransform: "none",
    marginLeft: 5,
    marginRight: 5,
    "&:hover": {
      color: "#36B516",
    },
  },
}));

export const Branch = (props: BranchProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [user] = useAuthenticationContext();
  const userId = user.id;
  const {
    id,
    name,
    address,
    countryId,
    cityId,
    latitude,
    longitude,
    manager,
    loadOverview,
  } = props;
  const [editing] = useState(false);
  const [selectedCountryId] = useState(countryId);
  const [selectedCityId] = useState(cityId);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [isSettingOwnerAsManager, setIsSettingOwnerAsManager] = useState(false);
  const [
    errorOnSettingOwnerAsManager,
    setErrorOnSettingOwnerAsManager,
  ] = useState(false);
  const [inviteModalOpened, setInviteModalOpened] = useState(false);

  const loadCountries = async () => {
    setCountries([]);
    try {
      const countries = await CountryService.supportedCountries();
      if (countries.isSuccess && countries.data.code === 100) {
        const responseData = JSON.parse(JSON.stringify(countries.data.data));
        setCountries(responseData);
      }
    } catch (_) {}
  };

  const loadCities = async () => {
    setCities([]);
    try {
      const cities = await CityService.supportedCities(selectedCountryId);
      if (cities.isSuccess && cities.data.code === 100) {
        const responseData = JSON.parse(JSON.stringify(cities.data.data));
        setCities(responseData);
      }
    } catch (_) {}
  };

  useEffect(() => {
    loadCountries();
    loadCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOwnerAsManager = async () => {
    setIsSettingOwnerAsManager(true);
    setErrorOnSettingOwnerAsManager(false);
    const setOwnerAsManagerResult = await BusinessOwnerService.setOwnerAsManager(
      {
        ownerId: userId,
        branchId: id,
      }
    );
    if (
      setOwnerAsManagerResult.isSuccess &&
      setOwnerAsManagerResult.data.code === 100
    ) {
      loadOverview();
    } else {
      setErrorOnSettingOwnerAsManager(true);
    }
    setIsSettingOwnerAsManager(false);
  };

  const toggleInviteModal = () => setInviteModalOpened(!inviteModalOpened);

  return (
    <>
      {inviteModalOpened && (
        <InviteManager
          branchId={id}
          isOpened={inviteModalOpened}
          closeCallback={toggleInviteModal}
        />
      )}
      <Grid className={classes.main} container spacing={2}>
        <Grid
          item
          container
          xs={12}
          sm={12}
          lg={12}
          justify="space-between"
          direction="row"
        >
          {!manager && errorOnSettingOwnerAsManager && (
            <Grid item xs={12} sm={12} lg={12}>
              <Alert
                severity="error"
                action={
                  <Button
                    onClick={setOwnerAsManager}
                    color="inherit"
                    size="small"
                  >
                    <FormattedMessage id="sharedScreen.tryAgain" />
                  </Button>
                }
              >
                <FormattedMessage id="sharedScreen.anErrorOccurredDuringManagerAssociation" />
              </Alert>
            </Grid>
          )}
          <Grid
            item
            container
            xs={6}
            sm={6}
            lg={6}
            direction="row"
            alignItems="center"
          >
            <Typography
              className={classes.formSectionTitle}
              variant="h4"
              component="h4"
            >
              {name}
            </Typography>
            {manager?.id === userId && (
              <div className={classes.managingContainer}>
                <Typography
                  className={classes.managing}
                  variant="h5"
                  component="h5"
                >
                  <FormattedMessage id="sharedScreen.managing" />
                </Typography>
              </div>
            )}
          </Grid>
          <Grid
            item
            container
            xs={6}
            sm={6}
            lg={6}
            direction="row"
            justify="flex-end"
          >
            {!manager && (
              <InputButton
                disabled={isSettingOwnerAsManager}
                color="secondary"
                variant="contained"
                onClick={setOwnerAsManager}
              >
                <FormattedMessage id="sharedScreen.manageThisBranch" />
                {isSettingOwnerAsManager && <CircularProgress size={30} />}
              </InputButton>
            )}
            {!manager && (
              <InputButton
                className={classes.inviteAManager}
                onClick={toggleInviteModal}
              >
                <FormattedMessage id="sharedScreen.inviteAManager" />
              </InputButton>
            )}
            <InputButton
              color="secondary"
              variant={editing ? "contained" : "outlined"}
            >
              <FormattedMessage id="sharedScreen.edit" />
            </InputButton>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} lg={12}>
          <InputSelect
            value={selectedCountryId}
            label={formatMessage({ id: "sharedScreen.country" })}
            fullWidth
            readOnly={!editing}
          >
            {countries.map((country) => (
              <MenuItem value={country.id}>{country.designation}</MenuItem>
            ))}
          </InputSelect>
        </Grid>
        <Grid item xs={12} sm={12} lg={12}>
          <InputSelect
            value={selectedCityId}
            label={formatMessage({ id: "sharedScreen.city" })}
            fullWidth
            readOnly={!editing}
          >
            {cities.map((city) => (
              <MenuItem value={city.id}>{city.designation}</MenuItem>
            ))}
          </InputSelect>
        </Grid>
        <Grid item xs={12} sm={12} lg={12}>
          <InputField
            value={address}
            label={formatMessage({ id: "sharedScreen.address" })}
            fullWidth
            readOnly={!editing}
            endAdornment={
              <InputAdornment position="end">
                <IconButton edge="end">
                  <LocationOnIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </Grid>
        <Grid item xs={12} sm={12} lg={12}>
          <InputField
            value={latitude}
            label={formatMessage({ id: "sharedScreen.latitude" })}
            fullWidth
            readOnly={!editing}
            endAdornment={
              <InputAdornment position="end">
                <IconButton edge="end">
                  <LocationOnIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </Grid>
        <Grid item xs={12} sm={12} lg={12}>
          <InputField
            value={longitude}
            label={formatMessage({ id: "sharedScreen.longitude" })}
            fullWidth
            readOnly={!editing}
            endAdornment={
              <InputAdornment position="end">
                <IconButton edge="end">
                  <LocationOnIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </Grid>
        <Grid item xs={12} sm={12} lg={12} />
        {(!manager || manager.id !== userId) && (
          <Grid item xs={12} sm={12} lg={12}>
            <Typography
              className={classes.formSectionTitle}
              variant="h4"
              component="h4"
            >
              <FormattedMessage id="sharedScreen.manager" />
            </Typography>
          </Grid>
        )}
        {!manager && (
          <Grid item xs={12} sm={12} lg={12}>
            <Typography
              className={classes.warningText}
              variant="h4"
              component="h4"
            >
              <FormattedMessage id="sharedScreen.noManagerRegistered" />
            </Typography>
          </Grid>
        )}
        {manager && manager.id !== userId && (
          <Grid item xs={12} sm={12} lg={12}>
            <InputField
              value={manager.name}
              label={formatMessage({ id: "sharedScreen.lastName" })}
              fullWidth
              readOnly
            />
          </Grid>
        )}
        {manager && manager.id !== userId && (
          <Grid item xs={12} sm={12} lg={12}>
            <InputField
              value={manager.phone}
              label={formatMessage({ id: "sharedScreen.phoneNumber" })}
              fullWidth
              readOnly
            />
          </Grid>
        )}
        {manager && manager.id !== userId && !manager.approved && (
          <Grid item xs={12} sm={12} lg={12}>
            <Typography
              className={classes.warningText}
              variant="h4"
              component="h4"
            >
              <FormattedMessage id="sharedScreen.notApprovedYet" />
            </Typography>
          </Grid>
        )}
      </Grid>
    </>
  );
};
